import { settingsPaths } from '@/paths';
import { CircleHelp, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Link, useParams } from 'react-router-dom';

const STORAGE_KEY = 'regulatory-watch-feed-missing-hint-dismissed';

type FeedMissingSubscriptionHintContextValue = {
  bubbleVisible: boolean;
  dismiss: () => void;
  toggleHelp: () => void;
};

const FeedMissingSubscriptionHintContext =
  createContext<FeedMissingSubscriptionHintContextValue | null>(null);

export const FeedMissingSubscriptionHintProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [bubbleVisible, setBubbleVisible] = useState(() => {
    if (typeof sessionStorage === 'undefined') {
      return true;
    }
    return sessionStorage.getItem(STORAGE_KEY) !== 'true';
  });

  const dismiss = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setBubbleVisible(false);
  }, []);

  const toggleHelp = useCallback(() => {
    if (bubbleVisible) {
      dismiss();
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
      setBubbleVisible(true);
    }
  }, [bubbleVisible, dismiss]);

  const value = useMemo(
    () => ({ bubbleVisible, dismiss, toggleHelp }),
    [bubbleVisible, dismiss, toggleHelp]
  );

  return (
    <FeedMissingSubscriptionHintContext.Provider value={value}>
      {children}
    </FeedMissingSubscriptionHintContext.Provider>
  );
};

export const FeedMissingSubscriptionHintOverlay: FC = () => {
  const ctx = useContext(FeedMissingSubscriptionHintContext);
  const { workspaceUname } = useParams<{ workspaceUname: string }>();

  if (!ctx || !workspaceUname) {
    return null;
  }

  const { bubbleVisible, dismiss, toggleHelp } = ctx;
  const subscriptionsHref = `/workspace/${workspaceUname}/settings/${settingsPaths.subscriptions}`;

  return (
    <div className="sticky top-0 z-30 h-0 overflow-visible pointer-events-none">
      <div className="absolute right-6 top-6 flex flex-col items-end gap-1 pointer-events-auto">
        <button
          type="button"
          onClick={toggleHelp}
          className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
          aria-label={
            bubbleVisible
              ? 'Dismiss subscription hint'
              : 'Show subscription hint'
          }
          aria-expanded={bubbleVisible}
        >
          <CircleHelp className="h-5 w-5" strokeWidth={1.75} />
        </button>
        {bubbleVisible ? (
          <div className="relative w-[min(100vw-3rem,260px)] max-w-[260px] rounded-lg border border-neutral-200 bg-white p-3 shadow-md">
            <button
              type="button"
              onClick={dismiss}
              className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
            <p className="pr-7 text-sm font-medium leading-tight text-neutral-900">
              Something missing?
            </p>
            <p className="mt-1 text-xs font-normal leading-snug text-neutral-900">
              Manage your{' '}
              <Link
                to={subscriptionsHref}
                className="text-blue-500 underline decoration-blue-500 hover:text-blue-600"
              >
                subscriptions
              </Link>
              .
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};
